import { sqliteTable, text, integer, index, type AnySQLiteColumn } from "drizzle-orm/sqlite-core";

// ============================================================================
// dimension_types — admin-defined analytic dimensions ("Campaign", "Region",
// "Customer Tier", …) — tree, company-scoped, code-unique-per-company.
//
// EPIC #412 Phase 3 — standalone dimensions.
//
// Phase 1/2 already supports doctype-based analytic tagging through the
// (referenceModule, referenceDoctype, referenceId) triple on
// `accounting_je_item_allocations`. Phase 3 adds an admin-managed dimension
// catalogue so users can tag GL/JE lines with values that DON'T correspond
// to an existing doctype (e.g. "Marketing Campaign 2026 Q1", "Bangkok",
// "Tier 1 customer"). This is the registry of dimension *kinds* — values
// live in `accounting_dimension_values` (`dimension-values.ts`).
//
// Tree structure mirrors `accounting_cost_centers` (parent_id self-ref +
// is_group flag) so the admin UI can group dimensions under category folders.
// Soft-delete columns mirror cost-centers as well — every CRUD pattern lines
// up so the UI can be borrowed wholesale.
// ============================================================================

export const dimensionTypes = sqliteTable("accounting_dimension_types", {
  id: text("id").primaryKey(), // ULID
  companyId: text("company_id").notNull(),
  code: text("code").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  parentId: text("parent_id").references((): AnySQLiteColumn => dimensionTypes.id), // self-ref tree
  isGroup: integer("is_group").notNull().default(0),
  isActive: integer("is_active").notNull().default(1),
  createdBy: text("created_by"),
  createdAt: text("created_at").notNull(),
  updatedBy: text("updated_by"),
  updatedAt: text("updated_at").notNull(),
  deletedBy: text("deleted_by"),
  deletedAt: text("deleted_at"),
}, (table) => ({
  // Company-scoped active lookup — every list-view query hits this.
  dimTypeCompanyIdx: index("dim_type_company_idx").on(table.companyId, table.isActive),
  // Tree traversal.
  dimTypeParentIdx: index("dim_type_parent_idx").on(table.parentId),
}));

// Type exports
export type DimensionType = typeof dimensionTypes.$inferSelect;
export type NewDimensionType = typeof dimensionTypes.$inferInsert;
